import { Text } from '@mantine/core';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	useTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { getAllAnnouncements } from '@apple/features/announcements/api/management';
import type { Announcement } from '@apple/features/announcements/models/management';
import { requireAuth } from '@apple/features/auth/utils/require-auth';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const defaultFilters: { name?: string } = {};
const defaultSorting: SortingState = [{ id: 'createdDate', desc: true }];
export const Route = createFileRoute('/_authed/_admin/content/announcements')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ContentManagement',
		});

		return {
			announcementsQueryOptions: queryOptions({
				queryKey: ['announcements'],
				queryFn: () => getAllAnnouncements(),
				placeholderData: keepPreviousData,
				throwOnError: true,
				initialData: [],
			}),
		};
	},
	loader: ({ context: { queryClient, announcementsQueryOptions } }) => {
		void queryClient.prefetchQuery(announcementsQueryOptions);
	},
	component: AnnouncementsManagePageRoute,
});

function AnnouncementsManagePageRoute() {
	const { t } = useTranslation('content');
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const { announcementsQueryOptions } = Route.useRouteContext();
	const announcementsQuery = useQuery(announcementsQueryOptions);

	const tableState = useTableState<Announcement, { name?: string }>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
	});

	const columnHelper = createDataTableColumnHelper<Announcement, Announcement>();
	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor('name', {
					enableSorting: false,
					header: t('announcements.headers.name'),
					cell: ({ row }) => (
						<Link
							to='/content/announcements/$announcementId'
							params={{ announcementId: row.original.contentGroupId! }}
							children={row.original.name}
						/>
					),
				}),
				columnHelper.accessor('active', {
					header: t('announcements.headers.status'),
					enableSorting: false,
					cell: ({ row }) => {
						const status = row.original.active
							? t('announcements.labels.active')
							: t('announcements.labels.inactive');
						return <Text>{status}</Text>;
					},
				}),
				columnHelper.accessor('createdDate', {
					header: t('announcements.headers.createdDate'),
					cell: ({ row }) => <FormattedDate value={row.original.createdDate} humanize />,
				}),
			]),
		[columnHelper, t],
	);

	const table = useTable({
		features,
		data: announcementsQuery.data,
		...tableState,
		enableFilters: false,
		enableColumnFilters: false,
		enableSorting: true,
		columnResizeMode: 'onChange',
		columns: columns,
	});

	return (
		<TableLayout
			title={t('announcements.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('announcements.buttons.addAnnouncement')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/content/announcements/add',
							viewTransition: false,
						})
					}
				/>,
			]}
		>
			<DataTable table={table} loading={announcementsQuery.isFetching} />
		</TableLayout>
	);
}
